// Where a received file's bytes go (docs/crypto-spec.md §6, ADR 0011).
//
// - Disk: the File System Access API (Chrome, Edge, Opera). The recipient
//   picks a folder (or, for one file, a save location) before anything is
//   sent, and each chunk is written as it arrives, so any size works.
// - Memory: everywhere else. Chunks collect into a Blob; when a file is
//   complete the page offers it with a Save link. Browsers may keep large
//   Blobs on disk themselves, but this is bounded by the device.
//
// Names come from the sender, so they're cleaned first (threat model T17),
// and a disk folder never has an existing file overwritten.

import type { FileEntry } from './fileTransfer';
import { safeFileName } from './tools';

export interface SavedFile {
  name: string;
  size: number;
  /** Memory sinks only: an object URL to save it from. Revoke when done. */
  url?: string;
}

export interface FileSink {
  readonly kind: 'disk' | 'memory';
  /** Start the next file. Files arrive in manifest order. */
  begin(file: FileEntry): Promise<void>;
  write(data: Uint8Array): Promise<void>;
  end(): Promise<SavedFile>;
  /** Stop and discard the file in progress. */
  abort(): Promise<void>;
}

// The parts of the File System Access API used here (not in every lib.dom).
interface Writable {
  write(data: BufferSource): Promise<void>;
  close(): Promise<void>;
  abort(): Promise<void>;
}
interface FileHandle {
  readonly name: string;
  createWritable(): Promise<Writable>;
}
interface DirectoryHandle {
  getFileHandle(name: string, options?: { create?: boolean }): Promise<FileHandle>;
}
interface Pickers {
  showDirectoryPicker?: (options?: { mode?: 'read' | 'readwrite'; id?: string }) => Promise<DirectoryHandle>;
  showSaveFilePicker?: (options?: { suggestedName?: string; id?: string }) => Promise<FileHandle>;
}

const pickers = (): Pickers => window as unknown as Pickers;

/** Whether this browser can write straight to disk. */
export const canSaveToDisk = (): boolean => typeof pickers().showDirectoryPicker === 'function' && typeof pickers().showSaveFilePicker === 'function';

/** "report.pdf" → "report (2).pdf". */
export const numberedName = (name: string, n: number): string => {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? `${name.slice(0, dot)} (${n})${name.slice(dot)}` : `${name} (${n})`;
};

const exists = async (dir: DirectoryHandle, name: string): Promise<boolean> => {
  try {
    await dir.getFileHandle(name);
    return true;
  } catch {
    return false;
  }
};

class DiskSink implements FileSink {
  readonly kind = 'disk';
  private writable: Writable | null = null;
  private current: { name: string; size: number } | null = null;
  private written = 0;

  constructor(private readonly target: { dir: DirectoryHandle } | { file: FileHandle }) {}

  async begin(file: FileEntry): Promise<void> {
    let handle: FileHandle;
    if ('file' in this.target) {
      handle = this.target.file;
    } else {
      const base = safeFileName(file.name);
      let name = base;
      for (let n = 2; await exists(this.target.dir, name); n += 1) name = numberedName(base, n);
      handle = await this.target.dir.getFileHandle(name, { create: true });
    }
    this.writable = await handle.createWritable();
    this.current = { name: handle.name, size: file.size };
    this.written = 0;
  }

  async write(data: Uint8Array): Promise<void> {
    await this.writable?.write(new Uint8Array(data));
    this.written += data.length;
  }

  async end(): Promise<SavedFile> {
    await this.writable?.close();
    this.writable = null;
    return { name: this.current?.name ?? '', size: this.written };
  }

  async abort(): Promise<void> {
    await this.writable?.abort().catch(() => undefined);
    this.writable = null;
  }
}

class MemorySink implements FileSink {
  readonly kind = 'memory';
  private parts: BlobPart[] = [];
  private current: FileEntry | null = null;
  private written = 0;

  async begin(file: FileEntry): Promise<void> {
    this.parts = [];
    this.current = file;
    this.written = 0;
  }

  async write(data: Uint8Array): Promise<void> {
    // A Blob per chunk lets the browser move them out of the JS heap.
    this.parts.push(new Blob([new Uint8Array(data)]));
    this.written += data.length;
  }

  async end(): Promise<SavedFile> {
    // Untyped: a sender-supplied MIME type mustn't decide how the browser treats it.
    const blob = new Blob(this.parts, { type: 'application/octet-stream' });
    this.parts = [];
    return { name: safeFileName(this.current?.name ?? ''), size: this.written, url: URL.createObjectURL(blob) };
  }

  async abort(): Promise<void> {
    this.parts = [];
  }
}

/** Ask where to save (one file: a save dialog; several: a folder). Null if the person cancels. Needs a click. */
export const pickDiskSink = async (files: readonly FileEntry[]): Promise<FileSink | null> => {
  const { showDirectoryPicker, showSaveFilePicker } = pickers();
  try {
    if (files.length === 1 && showSaveFilePicker) {
      return new DiskSink({ file: await showSaveFilePicker({ suggestedName: safeFileName(files[0]?.name ?? ''), id: 'sss-receive' }) });
    }
    if (showDirectoryPicker) return new DiskSink({ dir: await showDirectoryPicker({ mode: 'readwrite', id: 'sss-receive' }) });
  } catch {
    // Cancelled, or permission refused.
  }
  return null;
};

export const memorySink = (): FileSink => new MemorySink();

/*
 * OPFS (origin private file system): for browsers without disk saving
 * (Firefox, Safari), a stored drop's files are written into storage only
 * this site can see, a chunk at a time, then handed to the browser's own
 * download as a disk-backed File, so nothing big is held in memory
 * (docs/adr/0015). Counts against the site's storage quota, and the copy to
 * Downloads needs as much again; the page checks before starting. Cleared
 * when the page is left.
 */
const OPFS_DIR = 'sss-drops';

interface OpfsFileHandle extends FileHandle {
  getFile(): Promise<File>;
}
interface OpfsDirectory {
  getDirectoryHandle(name: string, options?: { create?: boolean }): Promise<OpfsDirectory>;
  getFileHandle(name: string, options?: { create?: boolean }): Promise<OpfsFileHandle>;
  removeEntry(name: string, options?: { recursive?: boolean }): Promise<void>;
}

const opfsRoot = async (): Promise<OpfsDirectory | null> => {
  try {
    return (await (navigator.storage as unknown as { getDirectory?: () => Promise<OpfsDirectory> }).getDirectory?.()) ?? null;
  } catch {
    return null;
  }
};

/** Whether this browser can write files into OPFS as a stream. */
export const canSaveToOpfs = (): boolean =>
  typeof (navigator.storage as unknown as { getDirectory?: unknown } | undefined)?.getDirectory === 'function' &&
  typeof (globalThis as { FileSystemFileHandle?: { prototype: { createWritable?: unknown } } }).FileSystemFileHandle?.prototype.createWritable === 'function';

class OpfsSink implements FileSink {
  readonly kind = 'memory';
  private writable: Writable | null = null;
  private handle: OpfsFileHandle | null = null;
  private current: FileEntry | null = null;
  private written = 0;
  private count = 0;

  constructor(private readonly dir: OpfsDirectory) {}

  async begin(file: FileEntry): Promise<void> {
    // Numbered, so two files with one name never collide; the Save link carries the real name.
    this.count += 1;
    this.handle = await this.dir.getFileHandle(`${this.count}`, { create: true });
    this.writable = await this.handle.createWritable();
    this.current = file;
    this.written = 0;
  }

  async write(data: Uint8Array): Promise<void> {
    await this.writable?.write(new Uint8Array(data));
    this.written += data.length;
  }

  async end(): Promise<SavedFile> {
    await this.writable?.close();
    this.writable = null;
    const file = (await this.handle?.getFile()) as File;
    return { name: safeFileName(this.current?.name ?? ''), size: this.written, url: URL.createObjectURL(new Blob([file], { type: 'application/octet-stream' })) };
  }

  async abort(): Promise<void> {
    await this.writable?.abort().catch(() => undefined);
    this.writable = null;
  }
}

/** A fresh OPFS folder for one download, or null where OPFS can't stream. */
export const opfsSink = async (key: string): Promise<FileSink | null> => {
  if (!canSaveToOpfs()) return null;
  const root = await opfsRoot();
  if (!root) return null;
  try {
    const all = await root.getDirectoryHandle(OPFS_DIR, { create: true });
    await all.removeEntry(key, { recursive: true }).catch(() => undefined);
    return new OpfsSink(await all.getDirectoryHandle(key, { create: true }));
  } catch {
    return null;
  }
};

/** Remove every file a stored-drop download left in OPFS. */
export const clearOpfs = async (): Promise<void> => {
  const root = await opfsRoot();
  await root?.removeEntry(OPFS_DIR, { recursive: true }).catch(() => undefined);
};

/** Free storage this site may use, in bytes, or null if the browser won't say. */
export const freeStorage = async (): Promise<number | null> => {
  try {
    const { quota, usage } = await navigator.storage.estimate();
    return quota === undefined ? null : quota - (usage ?? 0);
  } catch {
    return null;
  }
};

/** Ask to keep this site's storage (Firefox raises its limit from 10 GiB when granted, and asks the person). */
export const askPersistentStorage = async (): Promise<boolean> => {
  try {
    return (await navigator.storage.persist?.()) ?? false;
  } catch {
    return false;
  }
};
